/**
 * @module components/PrivateRoute
 * @description Higher-order route component for authentication and authorization.
 * Restricts access to authenticated users and optionally admin-only routes.
 */

import React from 'react';
import { get } from 'modules/utils';
import Login from '../auth/Login';
import { useIdentity } from '../providers/IdentityProvider';

/**
 * @typedef PrivateRouteProps
 * @prop {string|ReactNode} as render component as given instance or name
 *  - can be used to compose components
 * @prop {boolean} adminOnly if route is "admin only"
 *  - will check user roles to ensure only admins can reach
 *  - if not logged in, will navigate to `/` (unless already at `/`)
 *  - if not authorized for route, display `Login`
 * @prop {*} props rest of props (spread, will be passed to `as` / `Component`)
 */

/**
 * @component PrivateRoute
 * @type {React.FC<PrivateRouteProps>}
 * @summary Route wrapper enforcing authentication and optional admin authorization.
 * Redirects unauthenticated users to home, shows login for unauthorized users.
 * @param {PrivateRouteProps} props - Component props
 * @returns {React.ReactElement} Authorized component or Login page
 */
function PrivateRoute({ as: Component, adminOnly, ...props }) {
  const { isLoggedIn, user } = useIdentity();
  const { uri, navigate } = props;

  let authorized = isLoggedIn;

  if (adminOnly) {
    const roles = get(user, 'app_metadata.roles', null);
    authorized = roles && roles.includes('admin');
  }

  React.useEffect(() => {
    if (!isLoggedIn && uri !== '/') {
      navigate('/', { replace: true });
    }
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  return authorized ? <Component {...props} /> : <Login />;
}

export default PrivateRoute;
